import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { moduleInvoiceQueryKey } from "components/ModulePayments/moduleFinanceQueries";
import { getApiError } from "utils/apiError";
import ModuleInvoiceFormBody from "./ModuleInvoiceFormBody";
import { fetchModuleInvoice, fetchModuleInvoiceDraft } from "./moduleInvoiceApi";
import { formFromDraft, formFromInvoice } from "./moduleInvoiceForm";
import { hasVerifiedPayment, NO_EDIT_NOTICE, PAID_LOCKED_NOTICE } from "./moduleInvoiceRules";

const Notice = ({ severity, children, onClose }) => (
  <>
    <DialogContent>
      <Alert severity={severity}>{children}</Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </>
);

/**
 * Mounted only while open, and waits for its own fetch: a draft cached from an earlier opening would
 * seed the form with charges that may since have changed.
 */
const DialogLoader = ({ mode, moduleType, moduleId, onClose, ...rest }) => {
  const source = useQuery({
    queryKey: [...moduleInvoiceQueryKey(moduleType, moduleId), mode],
    queryFn: () =>
      mode === "create"
        ? fetchModuleInvoiceDraft(moduleType, moduleId)
        : fetchModuleInvoice(moduleType, moduleId),
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!source.isFetchedAfterMount) {
    return (
      <Box className="flex justify-center py-10">
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (source.isError) {
    return (
      <Notice severity="error" onClose={onClose}>
        {getApiError(source.error, "Could not load the invoice")}
      </Notice>
    );
  }
  if (mode === "edit" && !source.data?.canEdit) {
    return (
      <Notice severity="info" onClose={onClose}>
        {hasVerifiedPayment(source.data?.invoice) ? PAID_LOCKED_NOTICE : NO_EDIT_NOTICE}
      </Notice>
    );
  }
  const initialForm =
    mode === "create" ? formFromDraft(source.data) : formFromInvoice(source.data?.invoice);
  return (
    <ModuleInvoiceFormBody
      mode={mode}
      moduleType={moduleType}
      moduleId={moduleId}
      initialForm={initialForm}
      onClose={onClose}
      {...rest}
    />
  );
};

const ModuleInvoiceDialog = ({ open, onClose, ...props }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    {open && <DialogLoader onClose={onClose} {...props} />}
  </Dialog>
);

export default ModuleInvoiceDialog;
